import { useState, type FormEvent } from "react";
import { useTranslate } from "@refinedev/core";
import { PasswordField, Button, LockIcon } from "@fawkes/ui";

import { getAccessToken } from "../auth/tokenStore";
import { setMustChangePassword } from "../auth/mustChangePassword";

import "./LoginPage.css";
import "./SetPasswordPage.css";

// Same base-URL convention as dataProvider/authProvider: the Vite dev server proxies /api to the API.
const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

/**
 * The forced change-password screen: shown instead of the app when the session reports
 * `must_change_password` (an admin set this password and flagged it). It posts to the AUTHENTICATED
 * `POST /api/auth/change-password` — the user is signed in and proves the current password, which is
 * why this is not the anonymous `/set-password` token flow.
 *
 * Clearing the flag on success is what returns the user to the app; the shell re-renders on it. There
 * is deliberately no skip: an admin-chosen password is a shared secret until it is replaced. "Uitloggen"
 * is the only way past, and it leaves the flag set for the next login.
 */
export function ForcePasswordChangePage({ onLogout }: { onLogout: () => void }) {
  const t = useTranslate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!password || password !== confirm) {
      setError(t("forcePasswordChange.error.confirmMismatch"));
      return;
    }

    setSubmitting(true);
    let response: Response;
    try {
      response = await fetch(`${API_URL}/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${getAccessToken() ?? ""}`,
        },
        credentials: "include",
        body: JSON.stringify({ current_password: currentPassword, new_password: password }),
      });
    } catch {
      setSubmitting(false);
      setError(t("forcePasswordChange.error.unreachable"));
      return;
    }
    setSubmitting(false);

    if (response.status === 204) {
      // Dropping the flag is the navigation: the shell renders the app on the next paint.
      setMustChangePassword(false);
      return;
    }
    if (response.status === 422) {
      setError(t("forcePasswordChange.error.weakPassword"));
      return;
    }
    if (response.status === 401) {
      setError(t("forcePasswordChange.error.badCurrentPassword"));
      return;
    }
    setError(t("forcePasswordChange.error.generic"));
  };

  return (
    <div className="login-main">
      <form className="login-card set-password-card" onSubmit={onSubmit}>
        <div className="login-header">
          <img className="login-title-icon" src="/login/login-lock.png" alt="" aria-hidden="true" />
          <h1 className="login-title">{t("forcePasswordChange.title")}</h1>
        </div>

        <p className="set-password-intro">{t("forcePasswordChange.intro")}</p>

        {error && (
          <p className="login-error" role="alert">
            {error}
          </p>
        )}

        <PasswordField
          label={t("forcePasswordChange.currentPassword")}
          icon={<LockIcon />}
          autoComplete="current-password"
          autoFocus
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          revealLabel={t("login.showPassword")}
          hideLabel={t("login.hidePassword")}
        />
        <PasswordField
          label={t("forcePasswordChange.newPassword")}
          icon={<LockIcon />}
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          revealLabel={t("login.showPassword")}
          hideLabel={t("login.hidePassword")}
        />
        <PasswordField
          label={t("forcePasswordChange.confirmPassword")}
          icon={<LockIcon />}
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          revealLabel={t("login.showPassword")}
          hideLabel={t("login.hidePassword")}
        />

        <Button type="submit" variant="primary" disabled={submitting} className="login-submit">
          {t("forcePasswordChange.submit")}
        </Button>
        <Button type="button" variant="secondary" className="login-submit" onClick={onLogout}>
          {t("forcePasswordChange.logout")}
        </Button>
      </form>
    </div>
  );
}
